import { useMemo } from 'react';

import { ROUTE_PERMISSIONS } from '@/modules/authorization/config/route-permissions';
import { evaluatePermissionRequirement } from '@/modules/authorization/types/permission-requirement';
import { useIsFeatureEnabled, usePermissions } from '@/modules/common/hooks';
import { useAvailableSettings } from '@/modules/settings';
import { SettingType } from '@/modules/settings/types/setting-type';
import { AuthenticatedRoutes, Routes } from '@/router/routes';

interface RoutePermissions {
  canAccessRoute: (route: Routes) => boolean;
  // The route to redirect to if user tries to access a route they don't have permission for.
  // Resolves to the first sidebar page the user can actually open, falling back to Forbidden.
  accessibleRedirectRoute: AuthenticatedRoutes;
  isLoading: boolean;
}

/**
 * Primary navigable routes in sidebar order. When a user lands on a route they
 * can't access (e.g. Planner by default but they only have Time Tracking), we
 * redirect them to the first entry here they're allowed to open — mirroring the
 * order the sidebar lists them in (see app-sidebar `getSidebarItems`).
 */
const PRIMARY_ROUTES_IN_SIDEBAR_ORDER: AuthenticatedRoutes[] = [
  AuthenticatedRoutes.TimeTracking,
  AuthenticatedRoutes.TimeTrackingApprovals,
  AuthenticatedRoutes.TimeOff,
  AuthenticatedRoutes.Planner,
  AuthenticatedRoutes.Team,
  AuthenticatedRoutes.Projects,
  AuthenticatedRoutes.Clients,
  AuthenticatedRoutes.Invoices,
  AuthenticatedRoutes.Bills,
  AuthenticatedRoutes.Overheads,
];

/**
 * Hook that provides utilities for checking route permissions
 */
export function useRoutePermissions(): RoutePermissions {
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const { data: availableSettings, isLoading: isLoadingSettings } = useAvailableSettings();
  const isFeatureEnabled = useIsFeatureEnabled();

  const isLoading = isLoadingPermissions || isLoadingSettings;

  /**
   * Checks if the user has permission to access a specific route
   */
  const canAccessRoute = useMemo(
    () => (route: Routes) => {
      // Check if route has specific permission requirements
      const requirement = ROUTE_PERMISSIONS[route];
      if (!requirement) {
        // No specific requirement = accessible to authenticated users
        return true;
      }

      // Create a function to check if user has access to a setting
      const hasSettingAccess = (settingType: SettingType) => {
        return availableSettings?.includes(settingType) ?? false;
      };

      // Evaluate the permission requirement with feature flags and settings
      return evaluatePermissionRequirement(
        requirement,
        hasPermission,
        isFeatureEnabled,
        hasSettingAccess
      );
    },
    [hasPermission, availableSettings, isFeatureEnabled]
  );

  // First sidebar page the user can open; Forbidden if they can access none.
  const accessibleRedirectRoute = useMemo(
    () =>
      PRIMARY_ROUTES_IN_SIDEBAR_ORDER.find(route => canAccessRoute(route)) ??
      AuthenticatedRoutes.Forbidden,
    [canAccessRoute]
  );

  return {
    canAccessRoute,
    accessibleRedirectRoute,
    isLoading,
  };
}
